import { ChangeEvent, useEffect, useMemo, useRef } from 'react';

import { handleGrouping } from 'utilitys/helpers/general';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useUpdateIfReady } from 'components/Dashboards/Reporting/widgetInReport/hooks/RefreshWidgetPayload/useUpdateIfReady';
import { Dataset } from 'components/widgets/Trends/Trends';
import {
    getDataSetBySelectedItem,
    getPreDefinedCrossVersionsData,
    getPreDefinedListObj,
    mapEntries,
    mapOptions,
    preDefinedList,
} from 'components/widgets/Trends/Trends.utils';
import { SingleLineOptions } from 'components/widgets/Trends/components/TrendsGraph/TrendsGraph';

type OptionsWithNot = ISelectOption<string> & { note?: string };

const CHART_HEIGHT_IN_REPORT_VIEW = '100%';
const CHART_HEIGHT_IN_PAGE_VIEW = 760;

export const useTrendsLogic = ({ ui, api, widgetId, widgetViewMode, setIsPayloadReady }) => {
    const { sendEventWithDimensions } = useEventWithDimensions();
    useUpdateIfReady({ isPayloadReadyCallback: isPayloadReady, widgetViewMode: widgetViewMode });

    function isPayloadReady() {
        const currentPayload = api.crossVersionsTrends.payload;
        if (!currentPayload) return;
        if (
            currentPayload.ids === undefined ||
            currentPayload.ids.length === 0 ||
            currentPayload.ids[0] === null ||
            currentPayload.ids[0] === undefined
        ) {
            return;
        }
        setIsPayloadReady && setIsPayloadReady(true);
    }

    const groupedData = useMemo(() => {
        let crossVersionsData = {};
        if (api.crossVersionsTrends.instance.data) {
            switch (ui.state.singleLine) {
                case 'Single Parameter (Actual value)':
                    crossVersionsData = api.crossVersionsTrends.instance.data.actual?.graph;

                    break;
                case 'Multi Parameters (Normalized values)':
                    crossVersionsData = api.crossVersionsTrends.instance.data.normalized?.graph;

                    break;
                case 'Pre-defined Combinations (Normalized values)':
                    crossVersionsData = getPreDefinedCrossVersionsData(
                        api.crossVersionsTrends.instance.data.normalized?.graph,
                        preDefinedList,
                    );
                    break;
            }
        }
        return crossVersionsData ? handleGrouping(Object.values(crossVersionsData), 'title') : undefined;
    }, [api.crossVersionsTrends.instance.data, ui.state.singleLine]);

    const { graphData, options } = useMemo(() => {
        let graphData = { dataset: [] as Dataset[] };
        let options = [] as unknown as ISelectOption<string> | OptionsWithNot[];

        if (api.crossVersionsTrends.instance.data && ui.state.selectedOptions && groupedData) {
            const dataset = getDataSetBySelectedItem(
                ui.state.singleLine === 'Pre-defined Combinations (Normalized values)'
                    ? getPreDefinedListObj(ui.state.selectedOptions, preDefinedList)
                    : ui.state.selectedOptions,
                groupedData,
                ui.state.singleLine,
            );

            graphData = { dataset };

            switch (ui.state.singleLine) {
                case 'Single Parameter (Actual value)':
                    options = Object.entries(groupedData).map(mapEntries);
                    break;
                case 'Multi Parameters (Normalized values)':
                    options = Object.entries(groupedData).map(mapEntries);
                    break;
                case 'Pre-defined Combinations (Normalized values)':
                    options = preDefinedList.map(mapOptions);
                    break;
            }
        }

        return {
            graphData,
            options,
        };
    }, [api.crossVersionsTrends.instance.data, groupedData, ui.state.singleLine, ui.state.selectedOptions]);

    const handleItemSelection = (value: ISelectOption<string> | ISelectOption<string>[]) => {
        const label = Array.isArray(value) ? value.map((item) => item.label).join(', ') : value.label;
        sendEventWithDimensions({
            category: 'Trends',
            action: 'drop-list selection',
            label,
        });
        ui.setters.setSelectedOptions(value);
    };

    const handleSingle = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        ui.setters.setSingleLine(value as SingleLineOptions);
        if (groupedData) {
            if (value === 'Pre-defined Combinations (Normalized values)') {
                const option = preDefinedList.filter((item) => item.selected).map(mapOptions)[0];
                ui.setters.setSelectedOptions(option);
            } else {
                const selectedByDefault = Object.entries(groupedData)
                    .filter((item: [string, any]) => {
                        return item[1][0].selectedByDefault;
                    })
                    .map(mapEntries);

                ui.setters.setSelectedOptions(selectedByDefault);
            }
        }
    };

    const handleSelectOptions = (value) => {
        handleItemSelection(value);
    };

    const chartHeight = WidgetViewModeUtils.isReportView(widgetViewMode)
        ? CHART_HEIGHT_IN_REPORT_VIEW
        : CHART_HEIGHT_IN_PAGE_VIEW;

    const prevSingleLineRef = useRef(ui.state.singleLine);

    useEffect(() => {
        if (groupedData && !widgetId) {
            const singleLineChanged = prevSingleLineRef.current !== ui.state.singleLine;
            prevSingleLineRef.current = ui.state.singleLine;

            // Check if selectedOptions actually has values (not just empty array/object)
            const hasSelectedOptions = Array.isArray(ui.state.selectedOptions)
                ? ui.state.selectedOptions.length > 0
                : Boolean(ui.state.selectedOptions);

            // Initial report-edit-mode render guard: keep saved selection unless `singleLine` changes
            // Skip if singleLine didn't change and we already have selected options
            if (!singleLineChanged && hasSelectedOptions) {
                return;
            }

            if (ui.state.singleLine === 'Pre-defined Combinations (Normalized values)') {
                const option = preDefinedList.filter((item) => item.selected).map(mapOptions)[0];
                ui.setters.setSelectedOptions(option);
            } else {
                const selectedByDefault = Object.entries(groupedData)
                    .filter((item: [string, any]) => {
                        return item[1][0].selectedByDefault;
                    })
                    .map(mapEntries);
                const defaultValue =
                    ui.state.singleLine === 'Single Parameter (Actual value)'
                        ? selectedByDefault[0]
                        : selectedByDefault;
                ui.setters.setSelectedOptions(defaultValue);
            }
        }
    }, [widgetId, groupedData, ui.state.singleLine]);
    return { logic: { graphData, options, handleSelectOptions, handleSingle, chartHeight } };
};
